/**
 * components/ReportSuccessMessage/ReportSuccessMessage.js
 * Composant autonome pour afficher le message de succès après génération du rapport
 */

import "./ReportSuccessMessage.css";

export class ReportSuccessMessage {
  constructor(options = {}) {
    this.options = {
      title: options.title || "Rapport généré avec succès !",
      fileName: options.fileName || "rapport-tests-metiers.html",
      fileSize: options.fileSize || "2.4 MB",
      processingTime: options.processingTime || "3.2 secondes",
      timestamp: options.timestamp || new Date().toLocaleString("fr-FR"),
      stats: options.stats || {
        totalFibers: 0,
        totalOK: 0,
        totalWarnings: 0,
        totalErrors: 0,
      },
      buttonText: options.buttonText || "Regénérer le rapport",
      onRegenerate: options.onRegenerate || null,
      ...options,
    };

    this.element = null;
  }

  /**
   * Lier les événements
   */
  bindEvents() {
    if (!this.element) return;

    const button = this.element.querySelector(".success-action-btn");
    if (button && this.options.onRegenerate) {
      button.addEventListener("click", () => {
        this.options.onRegenerate();
      });
    }
  }

  /**
   * Rendu des statistiques
   */
  renderStats() {
    const { stats } = this.options;

    return `
      <div class="stats-summary">
        <div class="stat-card">
          <div class="stat-value">${stats.totalFibers}</div>
          <div class="stat-label">Fibres analysées</div>
        </div>
        <div class="stat-card success">
          <div class="stat-value">${stats.totalOK}</div>
          <div class="stat-label">Conformes</div>
        </div>
        <div class="stat-card warning">
          <div class="stat-value">${stats.totalWarnings}</div>
          <div class="stat-label">Avertissements</div>
        </div>
        <div class="stat-card error">
          <div class="stat-value">${stats.totalErrors}</div>
          <div class="stat-label">Erreurs</div>
        </div>
      </div>
    `;
  }

  /**
   * Rendu des détails du rapport
   */
  renderReportDetails() {
    return `
      <div class="report-details">
        <div class="detail-row">
          <span class="detail-label">📄 Fichier généré :</span>
          <span class="detail-value file-name">${this.options.fileName}</span>
        </div>
        <div class="detail-row">
          <div class="detail-group">
            <span class="detail-label">📊 Taille du rapport :</span>
            <span class="detail-value">${this.options.fileSize}</span>
          </div>
          <div class="detail-group">
            <span class="detail-label">⏰ Généré le :</span>
            <span class="detail-value">${this.options.timestamp}</span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Rendu du composant
   */
  render() {
    return `
    <div class="report-success-message">
      <div class="success-celebration">🎉</div>
      
      <div class="success-header">
        <div class="success-icon"></div>
        <h3 class="title-md">${this.options.title}</h3>
      </div>
      
      <p class="success-message">Votre rapport HTML a été analysé et téléchargé automatiquement</p>
      
      ${this.renderReportDetails()}
      
      ${this.renderStats()}
      
      ${this.options.onRegenerate ? `<button class="success-action-btn">${this.options.buttonText}</button>` : ""}
    </div>
  `;
  }

  /**
   * Monter le composant
   */
  mount(selector) {
    const container = document.querySelector(selector);
    if (!container) {
      throw new Error(`ReportSuccessMessage container ${selector} not found`);
    }

    container.innerHTML = this.render();
    this.element = container.querySelector(".report-success-message");
    this.bindEvents();
  }

  /**
   * Démonter le composant
   */
  unmount() {
    if (this.element) {
      this.element.remove();
      this.element = null;
    }
  }

  /**
   * Mettre à jour les données
   */
  updateData(newOptions) {
    this.options = { ...this.options, ...newOptions };

    if (this.element) {
      // Re-render avec les nouvelles données
      const container = this.element.parentElement;
      container.innerHTML = this.render();
      this.element = container.querySelector(".report-success-message");
      this.bindEvents();
    }
  }
}
